import type React from 'react';
import { useState } from 'react';

import Container from '@mui/material/Container';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

import CloseIcon from 'assets/icons/icn _ 24px  _ close_empty.svg?react';
import { TransitionUp } from 'components/animations/Transition';
import StandardButton from 'components/buttons/StandardButton';
import FormErrorMessage from 'components/forms/FormErrorMessage';
import { useStyles } from 'components/overlays/doubles/styles';
import TMXOverlay from 'components/overlays/TMXOverlay';
import PairedPlayersPanel from 'components/panels/doubles/PairedPlayersPanel';
import UnpairedPlayersPanel from 'components/panels/doubles/UnpairedPlayersPanel';
import { Feature, featureEnabled } from 'config/clients';
import { useProgress } from 'hooks/tournament/useSaveTournament';
import { type ParticipantInterface } from 'typedefs/store/tmxTypes';
import { participantName } from 'utils/participant';

import { type IdNameParticipantsInterface, type PairedParticipantInterface } from './types';

interface CreateDoublesTeamsOverlayProps {
  combinedEligibilityLoading?: boolean;
  pairedParticipantsLocal?: PairedParticipantInterface[];
  getCombinedEligibility: (participantIdPairs: string[][]) => void;
  setPairedParticipantsLocal: React.Dispatch<React.SetStateAction<PairedParticipantInterface[]>>;
  isOpen?: boolean;
  pairedIndividualParticipants: ParticipantInterface[];
  unpairedDrawParticipants: ParticipantInterface[];
  closeDialog(): void;
}

const CreateDoublesTeamsOverlay: React.FC<React.PropsWithChildren<CreateDoublesTeamsOverlayProps>> = ({
  combinedEligibilityLoading,
  pairedParticipantsLocal,
  getCombinedEligibility,
  setPairedParticipantsLocal,
  isOpen,
  closeDialog,
  pairedIndividualParticipants,
  unpairedDrawParticipants,
}) => {
  const { classes } = useStyles();
  const { t } = useTranslation();

  const saveDoublesInProgress = useProgress('saveDoubleTeams');
  const isDialogOpen = saveDoublesInProgress ? true : isOpen;
  const paperProps = { style: { padding: 10, overflowY: 'unset' as any } };

  const unpairedDrawParticipantsIdsNames = (
    [
      ...unpairedDrawParticipants,
      ...(featureEnabled(Feature.MULTI_PAIRED_PARTICIPANTS)
        ? pairedIndividualParticipants.map((p) => ({ ...p, isPaired: true }))
        : []),
    ] as IdNameParticipantsInterface[]
  ).map((participant) => ({
    participantId: participant.participantId,
    name: participantName(participant),
    isPaired: participant.isPaired,
  }));

  const [unpairedParticipantsLocal, setUnpairedParticipantsLocal] = useState<IdNameParticipantsInterface[]>(
    unpairedDrawParticipantsIdsNames,
  );
  const disabledSave =
    !pairedParticipantsLocal ||
    pairedParticipantsLocal?.length === 0 ||
    !!pairedParticipantsLocal?.find((pair) => pair.individualParticipantsIds.length === 1);

  const handleCancel = () => {
    setPairedParticipantsLocal([]);
    setUnpairedParticipantsLocal(unpairedDrawParticipantsIdsNames);
    closeDialog();
  };
  const handleEnter = () => {
    setPairedParticipantsLocal([]);
    setUnpairedParticipantsLocal(unpairedDrawParticipantsIdsNames);
  };

  const checkEligibility = () => {
    const participantIdPairs = pairedParticipantsLocal.map(
      (pairedParticipant) => pairedParticipant.individualParticipantsIds,
    );
    getCombinedEligibility(participantIdPairs);
  };

  return (
    <TMXOverlay
      fullScreen
      open={isDialogOpen}
      PaperProps={paperProps}
      TransitionComponent={TransitionUp}
      TransitionProps={{ onEnter: handleEnter }}
      onClose={handleCancel}
    >
      <DialogTitle id="doubles-teams-overlay-title">
        <Grid container direction="row" justifyContent="space-between">
          <Grid item />
          <Grid item>
            <Typography className={classes.overlayTitle} variant="h1">
              {t('createDoublesTeams')}
            </Typography>
          </Grid>
          <Grid item>
            <CloseIcon className={classes.closeIcon} onClick={handleCancel} />
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent>
        <Container maxWidth="lg">
          <Grid container direction="row" spacing={4}>
            <Grid item className={classes.unpairedPlayersContainer} sm={6} xs={12}>
              <UnpairedPlayersPanel
                setPairedParticipants={setPairedParticipantsLocal}
                setUnpairedParticipants={setUnpairedParticipantsLocal}
                unpairedParticipants={unpairedParticipantsLocal}
              />
            </Grid>
            <Grid item sm={6} xs={12}>
              <PairedPlayersPanel
                pairedParticipants={pairedParticipantsLocal}
                setPairedParticipants={setPairedParticipantsLocal}
                setUnpairedParticipants={setUnpairedParticipantsLocal}
              />
            </Grid>
          </Grid>
        </Container>
      </DialogContent>
      <DialogActions>
        <Container maxWidth="lg">
          {pairedParticipantsLocal?.some((pair) => pair.eligibilityError) && (
            <FormErrorMessage classname={classes.eligibilityError} message={t('eligibilityError')} />
          )}
          <Grid container direction="row" justifyContent="flex-end" spacing={2}>
            <Grid item>
              <StandardButton onClick={handleCancel}>{t('ccl')}</StandardButton>
            </Grid>
            <Grid item>
              <StandardButton
                disabled={disabledSave}
                loading={saveDoublesInProgress || combinedEligibilityLoading}
                variant="primary"
                onClick={checkEligibility}
              >
                {t('saveItems')}
              </StandardButton>
            </Grid>
          </Grid>
        </Container>
      </DialogActions>
    </TMXOverlay>
  );
};

export default CreateDoublesTeamsOverlay;
